// Shared by the client (webpack CJS interop) and the server (require). No
// 'use strict' directive: CRA lints this path as a module, where the directive
// is redundant, and CI=true turns that warning into a build failure.
const FULL_ADMIN_ROLES = Object.freeze(['super_admin', 'admin']);
const COVERAGE_ROLES = Object.freeze(['super_admin', 'coverage_manager', 'admin']);
const INSTALLER_OVERSIGHT_ROLES = Object.freeze(['super_admin', 'supervisor', 'admin']);
const NO_FRONT_CAPABILITIES = Object.freeze([]);
const ASSIGNABLE_ROLES = Object.freeze([
  'super_admin',
  'admin',
  'supervisor',
  'coverage_manager',
  'catalogue_admin',
  'operator',
  'sales_director',
  'sales_supervisor',
  'sales_manager',
  'sales_agent',
  'sales_finance',
  'sales_auditor',
  'catalog_manager',
  'content_editor',
  'seo_manager',
  'marketing_manager',
  'marketer',
  'finance_manager',
  'accountant',
  'support_manager',
  'support_agent',
  'compliance_manager',
  'analyst',
  'auditor',
  'design_approver',
]);
const ALL_FRONT_CAPABILITIES = Object.freeze([
  'front.content.read',
  'front.content.write',
  'front.media.manage',
  'front.seo.read',
  'front.seo.write',
  'front.appearance.manage',
  'front.catalogue.read',
  'front.catalogue.write',
  'front.lead.read',
  'front.lead.write',
  'front.lead.assign',
  'front.order.read',
  'front.order.manage',
  'front.finance.read',
  'front.finance.manage',
  'front.settings.read',
  'front.settings.write',
]);
const FRONT_CAPABILITIES_BY_ROLE = Object.freeze({
  super_admin: ALL_FRONT_CAPABILITIES,
  admin: ALL_FRONT_CAPABILITIES,
  supervisor: NO_FRONT_CAPABILITIES,
  design_approver: NO_FRONT_CAPABILITIES,
  coverage_manager: NO_FRONT_CAPABILITIES,
  catalogue_admin: Object.freeze(['front.catalogue.read', 'front.catalogue.write']),
  operator: NO_FRONT_CAPABILITIES,
  sales_director: NO_FRONT_CAPABILITIES,
  sales_supervisor: NO_FRONT_CAPABILITIES,
  sales_manager: Object.freeze([
    'front.catalogue.read',
    'front.lead.read',
    'front.lead.write',
    'front.lead.assign',
    'front.order.read',
    'front.order.manage',
  ]),
  sales_agent: Object.freeze([
    'front.catalogue.read',
    'front.lead.read',
    'front.lead.write',
    'front.order.read',
    'front.order.manage',
  ]),
  sales_finance: NO_FRONT_CAPABILITIES,
  sales_auditor: NO_FRONT_CAPABILITIES,
  catalog_manager: Object.freeze([
    'front.catalogue.read',
    'front.catalogue.write',
    'front.media.manage',
  ]),
  content_editor: Object.freeze([
    'front.content.read',
    'front.content.write',
    'front.media.manage',
  ]),
  seo_manager: Object.freeze([
    'front.content.read',
    'front.content.write',
    'front.media.manage',
    'front.seo.read',
    'front.seo.write',
  ]),
  marketing_manager: Object.freeze([
    'front.content.read',
    'front.content.write',
    'front.media.manage',
    'front.appearance.manage',
  ]),
  marketer: Object.freeze(['front.content.read', 'front.content.write']),
  finance_manager: Object.freeze([
    'front.order.read',
    'front.order.manage',
    'front.finance.read',
    'front.finance.manage',
  ]),
  accountant: Object.freeze([
    'front.order.read',
    'front.order.manage',
    'front.finance.read',
  ]),
  support_manager: Object.freeze([
    'front.catalogue.read',
    'front.lead.read',
    'front.lead.write',
    'front.order.read',
    'front.order.manage',
  ]),
  support_agent: Object.freeze([
    'front.lead.read',
    'front.lead.write',
    'front.order.read',
    'front.order.manage',
  ]),
  compliance_manager: Object.freeze(['front.lead.read', 'front.order.read']),
  analyst: NO_FRONT_CAPABILITIES,
  auditor: Object.freeze(['front.lead.read', 'front.order.read', 'front.finance.read']),
});

function rolesFor(...capabilities) {
  return Object.freeze(ASSIGNABLE_ROLES.filter((role) => capabilities.some(
    (capability) => FRONT_CAPABILITIES_BY_ROLE[role].includes(capability),
  )));
}

const STORE_ROLES = rolesFor('front.catalogue.read');
const CATEGORY_ROLES = rolesFor(
  'front.content.read',
  'front.content.write',
  'front.catalogue.read',
  'front.catalogue.write',
);
const CONTENT_ROLES = rolesFor(
  'front.content.read',
  'front.media.manage',
  'front.seo.read',
  'front.appearance.manage',
);
const SALES_LEAD_ROLES = Object.freeze([
  ...new Set([
    ...rolesFor('front.lead.read'),
    'marketer',
    'sales_director',
    'sales_supervisor',
    'sales_auditor',
  ]),
]);
const SALES_ORDER_ROLES = Object.freeze([
  ...new Set([
    ...rolesFor('front.order.read'),
    'sales_director',
    'sales_supervisor',
    'sales_auditor',
  ]),
]);
const SALES_ROLES = Object.freeze([
  ...new Set([
    ...SALES_LEAD_ROLES,
    ...SALES_ORDER_ROLES,
    'sales_finance',
  ]),
]);
// Reading a commission ledger and approving a payout are the same audience.
// The API still separates sales.commission.* from sales.payout.*, so a reader
// here is not a writer there; this only decides who may open the screen.
// Deliberately narrower than the server's SALES_READ_ANY, which would also let
// an agent and a manager in: that narrowing predates W30 and is preserved.
const SALES_MONEY_ROLES = Object.freeze([
  'super_admin', 'admin', 'sales_director', 'sales_finance', 'sales_auditor',
]);
// Exactly the roles the server grants sales.representative.read to, so the tab
// appears when and only when the API will answer. SALES_ROLES is the wrong set
// here: it reaches accountants, support agents and auditors, none of whom hold
// that permission, and all of whom would meet a 403.
const SALES_NETWORK_ROLES = Object.freeze([
  'super_admin', 'admin', 'sales_director', 'sales_supervisor',
  'sales_manager', 'sales_agent', 'sales_finance', 'sales_auditor',
]);
const CATALOGUE_PANEL_ROLES = Object.freeze(['super_admin', 'catalogue_admin', 'admin']);
// The bundle and value-added screens live visually under «فروشگاه», but their
// API (/api/bundles) is mounted under the `catalogue` panel capability, not
// `store` - see PANEL_ROUTE_CAPABILITIES in server/config/routeMounts.js.
// Gating on STORE_ROLES alone would show the tab to a role that then gets a
// 403 from every request it makes. Intersect instead, so the tab appears
// exactly when the API will answer, and stays correct if either side changes.
const BUNDLE_ROLES = Object.freeze(
  STORE_ROLES.filter((role) => CATALOGUE_PANEL_ROLES.includes(role)),
);
const FINANCE_ROLES = Object.freeze([
  ...new Set([...rolesFor('front.finance.read', 'front.finance.manage'), 'sales_finance']),
]);
const SETTINGS_ROLES = rolesFor('front.settings.read');

const PANEL_CAPABILITIES = {
  carrier: FULL_ADMIN_ROLES,
  'madar': FULL_ADMIN_ROLES,
  'madar.map': FULL_ADMIN_ROLES,
  'madar.library': FULL_ADMIN_ROLES,
  'madar.imports': FULL_ADMIN_ROLES,
  'madar.reports': FULL_ADMIN_ROLES,
  'carrier.madar': FULL_ADMIN_ROLES,
  design: FULL_ADMIN_ROLES,
  'design.tariffs': FULL_ADMIN_ROLES,
  'design.profiles': FULL_ADMIN_ROLES,
  coverage: COVERAGE_ROLES,
  'coverage.setup': COVERAGE_ROLES,
  'coverage.imports': COVERAGE_ROLES,
  'coverage.conflicts': COVERAGE_ROLES,
  'coverage.publication': COVERAGE_ROLES,
  'coverage.versions': COVERAGE_ROLES,
  applicants: FULL_ADMIN_ROLES,
  catalogue: CATALOGUE_PANEL_ROLES,
  experts: FULL_ADMIN_ROLES,
  'experts.pending': FULL_ADMIN_ROLES,
  'experts.all': FULL_ADMIN_ROLES,
  'experts.reports': FULL_ADMIN_ROLES,
  installers: Object.freeze([...INSTALLER_OVERSIGHT_ROLES, 'design_approver']),
  'installers.measurements': Object.freeze(['super_admin', 'admin', 'design_approver']),
  'installers.civil-handover': INSTALLER_OVERSIGHT_ROLES,
  'installers.installers': FULL_ADMIN_ROLES,
  'installers.contractors': FULL_ADMIN_ROLES,
  'installers.jobs': INSTALLER_OVERSIGHT_ROLES,
  'installers.map': FULL_ADMIN_ROLES,
  'installers.inspection': FULL_ADMIN_ROLES,
  'installers.workflow': FULL_ADMIN_ROLES,
  oversight: INSTALLER_OVERSIGHT_ROLES,
  'oversight.council': INSTALLER_OVERSIGHT_ROLES,
  'oversight.strike': INSTALLER_OVERSIGHT_ROLES,
  'oversight.solo': INSTALLER_OVERSIGHT_ROLES,
  'oversight.rulebooks': FULL_ADMIN_ROLES,
  ai: FULL_ADMIN_ROLES,
  'ai.consultant': FULL_ADMIN_ROLES,
  'ai.ava': FULL_ADMIN_ROLES,
  'ai.ava.chat': FULL_ADMIN_ROLES,
  'ai.ava.history': FULL_ADMIN_ROLES,
  'ai.models': FULL_ADMIN_ROLES,
  // Reading a ticket means reading a customer's service history, so this is the
  // support desk plus the auditor, who stays read-only through the server grant
  // rather than through a hidden button.
  support: Object.freeze([
    'super_admin', 'admin', 'support_manager', 'support_agent', 'auditor',
  ]),
  // Role assignment lived inside «اپراتورهای شبکه», where a careful reviewer
  // looking for user management could not find it. Its own tab now; the
  // super_admin-only rule on changing a role is unchanged and enforced in
  // RoleEditor and on the server.
  users: FULL_ADMIN_ROLES,
  store: CATEGORY_ROLES,
  'store.operators': STORE_ROLES,
  'store.services': STORE_ROLES,
  'store.packages': STORE_ROLES,
  'store.charge': STORE_ROLES,
  'store.discounts': STORE_ROLES,
  'store.categories': CATEGORY_ROLES,
  'store.bundles': BUNDLE_ROLES,
  'store.vas': BUNDLE_ROLES,
  content: CONTENT_ROLES,
  'content.posts': rolesFor('front.content.read'),
  'content.media': rolesFor('front.media.manage'),
  'content.seo': rolesFor('front.seo.read'),
  'content.appearance': rolesFor('front.appearance.manage'),
  sales: SALES_ROLES,
  'sales.leads': SALES_LEAD_ROLES,
  'sales.orders': SALES_ORDER_ROLES,
  // A sales agent may open the representative list; the API answers it at
  // SCOPE.OWN so they see only their own row, and the screen labels the result
  // as partial rather than presenting it as the whole network.
  // Narrower than the network screens on purpose. The desk reads
  // GET /representatives expecting the caller's OWN row, and sales_agent is the
  // only role the server grants sales.representative.read at SCOPE.OWN - every
  // other sales role reads at ANY and would be shown the whole list dressed up
  // as "my desk". Full admins keep it for support.
  'sales.my-desk': Object.freeze(['super_admin', 'admin', 'sales_agent']),
  'sales.representatives': SALES_NETWORK_ROLES,
  'sales.teams': SALES_NETWORK_ROLES,
  'sales.commission': SALES_MONEY_ROLES,
  'sales.payouts': SALES_MONEY_ROLES,
  // Exactly the roles the server grants sales.lead.read, which is what the
  // reporting routes require. sales_agent and marketer hold it at SCOPE.OWN, so
  // their reports cover only their own records and the dashboard says so rather
  // than presenting a personal figure as a company one.
  'sales.reports': SALES_LEAD_ROLES,
  finance: FINANCE_ROLES,
  'finance.overview': Object.freeze([
    ...new Set([...rolesFor('front.finance.read'), 'sales_finance']),
  ]),
  'finance.refunds': rolesFor('front.finance.manage'),
  'finance.reconciliation': rolesFor('front.finance.read'),
  settings: SETTINGS_ROLES,
  'settings.site': SETTINGS_ROLES,
  'settings.shop': SETTINGS_ROLES,
  'settings.auth': SETTINGS_ROLES,
};

Object.defineProperties(PANEL_CAPABILITIES, {
  assignableRoles: { value: ASSIGNABLE_ROLES, enumerable: false },
  frontCapabilitiesFor: {
    value: (role) => FRONT_CAPABILITIES_BY_ROLE[role] || NO_FRONT_CAPABILITIES,
    enumerable: false,
  },
  canAccessPanelSection: {
    value: (role, section) => PANEL_CAPABILITIES[section]?.includes(role) === true,
    enumerable: false,
  },
});

module.exports = Object.freeze(PANEL_CAPABILITIES);
